import { type ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PAYMENT_METHODS,
  PERMISSIONS,
  type Locale,
  type OrderPaymentState,
  type OrderResponse,
  type PaymentMethod,
  type PaymentResponse,
  type PaymentStatus,
  requiresBankRef,
} from '@aps/contracts';
import { Button, formatMoney, formatSumInput, parseSumInput, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { can } from '../lib/session.js';

/**
 * To'lov usuli va holati yorliqlari.
 *
 * Ro'yxatlar KONTRAKTDAN. `Record` tipi tufayli enum'ga yangi qiymat
 * qo'shilsa va tarjimasi yozilmasa `tsc` yiqiladi — ekranda xom
 * `PAYME` chiqib qolmaydi.
 */
const METHOD_LABELS: Record<PaymentMethod, Record<Locale, string>> = {
  CASH: { UZ: 'Naqd', RU: 'Наличные' },
  CARD_TRANSFER: { UZ: "Kartaga o'tkazma", RU: 'Перевод на карту' },
  PAYME: { UZ: 'Payme', RU: 'Payme' },
  CLICK: { UZ: 'Click', RU: 'Click' },
};

const STATUS_LABELS: Record<PaymentStatus, Record<Locale, string>> = {
  PENDING: { UZ: 'Tasdiqlanmagan', RU: 'Не подтверждён' },
  CONFIRMED: { UZ: 'Tasdiqlangan', RU: 'Подтверждён' },
  REJECTED: { UZ: 'Rad etilgan', RU: 'Отклонён' },
  REFUNDED: { UZ: 'Qaytarilgan', RU: 'Возвращён' },
};

const STATE_LABELS: Record<OrderPaymentState, Record<Locale, string>> = {
  UNPAID: { UZ: "To'lanmagan", RU: 'Не оплачен' },
  PARTIAL: { UZ: 'Qisman', RU: 'Частично' },
  PAID: { UZ: "To'langan", RU: 'Оплачен' },
  OVERPAID: { UZ: 'Ortiqcha', RU: 'Переплата' },
};

/**
 * MVP da faqat naqd va kartaga o'tkazma ishlaydi. Payme va Click
 * `PaymentMethod` da bor (kelajak uchun), lekin integratsiyasi yo'q —
 * ularni formada ko'rsatish "shu yo'l ishlaydi" degan yolg'on ma'no
 * berardi. Mavjud qatorda usul KO'RSATILADI, faqat yangi yozib
 * bo'lmaydi.
 */
const RECORDABLE_METHODS = PAYMENT_METHODS.filter(
  (method) => method === 'CASH' || method === 'CARD_TRANSFER',
);

interface PaymentsPanelProps {
  order: OrderResponse;
}

/**
 * Buyurtmaning to'lovlari.
 *
 * Buyurtmaning to'lov holati QATORLARDAN hisoblanadi va serverdan
 * keladi (docs/02-MAHSULOT.md → "To'lov"). Bu yerda u qayta
 * hisoblanmaydi: ikki joyda hisoblansa ular ajralib ketadi va ekran
 * bazadagi haqiqatga zid raqam ko'rsatib turardi.
 */
export function PaymentsPanel({ order }: PaymentsPanelProps): ReactNode {
  const { locale, me } = useSession();
  const t = messages(locale);

  // Qoldiq — BigInt bilan. `Number` da 92 233 720 368 so'mdan katta
  // summa jimgina buziladi (avtopart-money skill).
  const remaining = BigInt(order.grandTotal) - BigInt(order.paidTotal);

  return (
    <>
      <Field label={t.orderItemsTotal} value={formatMoney(order.itemsTotal, locale)} />
      <Field label={t.orderDiscount} value={formatMoney(order.discountTotal, locale)} />
      <Field label={t.orderTotal} value={formatMoney(order.grandTotal, locale)} />
      <Field label={t.orderPaid} value={formatMoney(order.paidTotal, locale)} />
      <Field
        label={remaining < 0n ? t.paymentOverpaid : t.paymentRemaining}
        value={formatMoney((remaining < 0n ? -remaining : remaining).toString(), locale)}
      />
      <div className="adm-kv">
        <span className="adm-kv__key">{t.ordersFilterPayment}</span>
        <span className={`adm-badge adm-badge--${order.paymentState.toLowerCase()}`}>
          {STATE_LABELS[order.paymentState][locale]}
        </span>
      </div>

      {order.payments.length === 0 ? (
        <p className="aps-muted">{t.orderNoPayments}</p>
      ) : (
        <ul className="adm-payments">
          {order.payments.map((payment) => (
            <PaymentRow key={payment.id} payment={payment} orderId={order.id} />
          ))}
        </ul>
      )}

      {can(me, PERMISSIONS.PAYMENT_RECORD) && (
        <RecordForm orderId={order.id} suggested={remaining > 0n ? remaining.toString() : '0'} />
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="adm-kv">
      <span className="adm-kv__key">{label}</span>
      <span className="adm-kv__value">{value}</span>
    </div>
  );
}

/* ------------------------------ bitta qator ------------------------------ */

type RowMode = 'none' | 'confirm' | 'reject' | 'refund';

function PaymentRow({
  payment,
  orderId,
}: {
  payment: PaymentResponse;
  orderId: string;
}): ReactNode {
  const { locale, me } = useSession();
  const t = messages(locale);
  const [mode, setMode] = useState<RowMode>('none');

  const canConfirm = can(me, PERMISSIONS.PAYMENT_CONFIRM);
  const canRefund = can(me, PERMISSIONS.PAYMENT_REFUND);

  return (
    <li className="adm-payments__row">
      <div className="adm-payments__head">
        <span className="adm-payments__amount">{formatMoney(payment.amount, locale)}</span>
        <span className="aps-muted">{METHOD_LABELS[payment.method][locale]}</span>
        <span className={`adm-badge adm-badge--${payment.status.toLowerCase()}`}>
          {STATUS_LABELS[payment.status][locale]}
        </span>
        <span className="aps-muted">
          {new Date(payment.createdAt).toLocaleString(locale === 'RU' ? 'ru' : 'uz')}
        </span>
      </div>

      <div className="adm-payments__meta">
        {/* Dalil — KIM qabul qilgani. Naqdda bundan boshqa dalil yo'q. */}
        {payment.verifiedByName !== null && (
          <span className="aps-muted">
            {t.paymentVerifiedBy}: {payment.verifiedByName}
          </span>
        )}
        {payment.bankRef !== null && (
          <span className="aps-muted">
            {t.paymentBankRef}: {payment.bankRef}
          </span>
        )}
        {payment.cardLast4 !== null && <span className="aps-muted">•••• {payment.cardLast4}</span>}
        {/*
          Mijoz botdan yuborgan chek. U DALIL EMAS — tasdiqlash uchun
          baribir bank ma'lumotnomasi kerak (docs/02-MAHSULOT.md).
        */}
        {payment.receiptUrl !== null && <span className="aps-muted">{t.paymentHasReceipt}</span>}
        {payment.rejectReason !== null && (
          <span className="aps-muted">
            {t.paymentReason}: {payment.rejectReason}
          </span>
        )}
      </div>

      {mode === 'none' && (
        <div className="adm-payments__actions">
          {payment.status === 'PENDING' && canConfirm && (
            <>
              <Button
                size="sm"
                onClick={() => {
                  setMode('confirm');
                }}
              >
                {t.paymentConfirm}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setMode('reject');
                }}
              >
                {t.paymentReject}
              </Button>
            </>
          )}
          {payment.status === 'CONFIRMED' && canRefund && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setMode('refund');
              }}
            >
              {t.paymentRefund}
            </Button>
          )}
          {/*
            Operator qatorni yozadi, lekin tasdiqlay olmaydi (vazifalar
            ajratilishi). Unga tugma emas, NIMA kutilayotgani aytiladi —
            aks holda ekran "hech nima qilib bo'lmaydi" bo'lib ko'rinadi.
          */}
          {payment.status === 'PENDING' && !canConfirm && (
            <span className="aps-muted">{t.paymentAwaitingConfirmer}</span>
          )}
        </div>
      )}

      {mode !== 'none' && (
        <PaymentAction
          payment={payment}
          orderId={orderId}
          mode={mode}
          onDone={() => {
            setMode('none');
          }}
        />
      )}
    </li>
  );
}

/** Tasdiqlash / rad etish / qaytarish — uchalasi ham bitta forma shakli. */
function PaymentAction({
  payment,
  orderId,
  mode,
  onDone,
}: {
  payment: PaymentResponse;
  orderId: string;
  mode: Exclude<RowMode, 'none'>;
  onDone: () => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Qoida KONTRAKTDAN (`requiresBankRef`) — server bilan bir manbadan.
  // Bu yerda qaytadan yozilsa forma "majburiy emas" deb turgani holda
  // server 400 qaytarardi.
  const bankRefRequired = mode === 'confirm' && requiresBankRef(payment.method);
  const trimmed = text.trim();
  const blocked = (bankRefRequired && trimmed === '') || (mode !== 'confirm' && trimmed === '');

  const run = useMutation({
    mutationFn: () => {
      if (mode === 'confirm') {
        return api.confirmPayment(payment.id, trimmed === '' ? {} : { bankRef: trimmed });
      }
      if (mode === 'reject') return api.rejectPayment(payment.id, trimmed);
      return api.refundPayment(payment.id, trimmed);
    },
    onSuccess: () => {
      // Buyurtma qayta o'qilmasa `paidTotal` va holat eski bo'lib qoladi:
      // amal bajarilgandek ko'rinadi, ekran esa eski raqamni ko'rsatadi.
      void client.invalidateQueries({ queryKey: ['order', orderId] });
      void client.invalidateQueries({ queryKey: ['payment-queue'] });
      /*
       * Forma YOPILADI va qator navbatdan yo'qoladi — ekranda amal
       * bajarilganining izi qolmaydi va QAYSI amal bajarilgani ham
       * ko'rinmaydi. Tasdiqlash pul bilan bog'liq, shuning uchun u
       * alohida aytiladi.
       */
      toast.show(mode === 'confirm' ? t.toastPaymentConfirmed : t.toastSaved, 'success');
      onDone();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const label =
    mode === 'confirm' ? t.paymentConfirm : mode === 'reject' ? t.paymentReject : t.paymentRefund;

  return (
    <div className="adm-payments__form">
      <label className="aps-field">
        <span className="aps-field__label">
          {mode === 'confirm' ? t.paymentBankRef : t.paymentReason}
          {(bankRefRequired || mode !== 'confirm') && ' *'}
        </span>
        <input
          className="aps-field__input"
          value={text}
          maxLength={mode === 'confirm' ? 120 : 255}
          onChange={(event) => {
            setText(event.target.value);
            setError(null);
          }}
        />
        {bankRefRequired && <span className="aps-field__hint">{t.paymentBankRefHint}</span>}
      </label>

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      <div className="adm-payments__actions">
        <Button
          size="sm"
          disabled={blocked || run.isPending}
          onClick={() => {
            run.mutate();
          }}
        >
          {run.isPending ? t.statusApplying : label}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t.cancel}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------ yozish formasi ------------------------------ */

function RecordForm({ orderId, suggested }: { orderId: string; suggested: string }): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();

  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amount, setAmount] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [error, setError] = useState<string | null>(null);

  // So'm -> tiyin: yagona joyda (`parseSumInput`). Noto'g'ri kirishda
  // `null` — tugma bosilmaydi, so'rov umuman ketmaydi.
  const tiyin = parseSumInput(amount);

  const record = useMutation({
    mutationFn: () => {
      if (tiyin === null) throw new Error('amount');
      return api.recordPayment(orderId, {
        method,
        amount: tiyin,
        ...(method === 'CARD_TRANSFER' && cardLast4 !== '' ? { cardLast4 } : {}),
      });
    },
    onSuccess: () => {
      setOpen(false);
      setAmount('');
      setCardLast4('');
      void client.invalidateQueries({ queryKey: ['order', orderId] });
      void client.invalidateQueries({ queryKey: ['payment-queue'] });
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  if (!open) {
    return (
      <div className="adm-payments__actions">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            // Qolgan qarz — eng ehtimolli summa. Operator uni tuzatishi
            // mumkin, lekin ko'p hollarda shuni yozadi.
            setAmount(suggested === '0' ? '' : formatSumInput(suggested));
            setOpen(true);
          }}
        >
          {t.paymentRecord}
        </Button>
      </div>
    );
  }

  return (
    <div className="adm-payments__form">
      <p className="aps-field__hint">{t.paymentRecordHint}</p>

      <label className="aps-field">
        <span className="aps-field__label">{t.paymentMethod}</span>
        <select
          className="aps-field__input"
          value={method}
          onChange={(event) => {
            setMethod(event.target.value as PaymentMethod);
          }}
        >
          {RECORDABLE_METHODS.map((value) => (
            <option key={value} value={value}>
              {METHOD_LABELS[value][locale]}
            </option>
          ))}
        </select>
      </label>

      <label className="aps-field">
        <span className="aps-field__label">{t.paymentAmount} *</span>
        <input
          className="aps-field__input"
          inputMode="decimal"
          value={amount}
          onChange={(event) => {
            setAmount(event.target.value);
            setError(null);
          }}
        />
        <span className="aps-field__hint">
          {tiyin === null && amount.trim() !== ''
            ? t.paymentAmountInvalid
            : t.paymentAmountHint(formatMoney(tiyin ?? '0', locale))}
        </span>
      </label>

      {method === 'CARD_TRANSFER' && (
        <label className="aps-field">
          <span className="aps-field__label">{t.paymentCardLast4}</span>
          <input
            className="aps-field__input"
            inputMode="numeric"
            maxLength={4}
            value={cardLast4}
            onChange={(event) => {
              setCardLast4(event.target.value.replace(/\D/gu, ''));
            }}
          />
        </label>
      )}

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      <div className="adm-payments__actions">
        <Button
          size="sm"
          disabled={
            tiyin === null ||
            tiyin === '0' ||
            (method === 'CARD_TRANSFER' && cardLast4 !== '' && cardLast4.length !== 4) ||
            record.isPending
          }
          onClick={() => {
            record.mutate();
          }}
        >
          {record.isPending ? t.statusApplying : t.paymentRecordSave}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          {t.cancel}
        </Button>
      </div>
    </div>
  );
}
